import { Table, Button, TableColumn } from 'common/components';
import { FileText, SquarePlay, Phone } from 'lucide-react';

import { useGetCallsData } from 'services/hooks/queries';
import { ManualCallbacks as ManualCallbacksType } from 'services/api/getCallsData';
import { useState } from 'react';
import { CallDataType } from 'types/call.type';
import { StatusSelect } from './components/StatusSelect';
import { useAgentCallConsole } from 'components/AgentCallConsole/hooks/useAgentCallConsole';
import { CallInfo } from 'types/agentCommandCenter.type';

import styles from './ManualCallbacks.module.scss';

type ManualCallbacksProps = {
    onViewTranscript: (callId: string, callInfo: CallInfo) => void;
    onListenRecording: (callId: string, callInfo: CallInfo) => void;
};

export const ManualCallbacks = ({ onViewTranscript, onListenRecording }: ManualCallbacksProps) => {
    const { handleMakeCall } = useAgentCallConsole();
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const {
        callsData,
        isCallsDataLoading,
        isCallsDataFetching,
        refetch: refetchCallsData,
    } = useGetCallsData(
        {
            manualCallback: ManualCallbacksType.NEEDED,
            currentPage: currentPage,
            perPage: rowsPerPage,
        },
        true // isEnabled
    );

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(1);
    };

    const callbackHandler = (callId: string) => {
        handleMakeCall(callId, {
            onSuccess: () => {
                refetchCallsData();
            },
        });
    };

    const columns: TableColumn<CallDataType>[] = [
        {
            key: 'callTime',
            title: 'Date',
            render: (_, record) => (
                <span>{new Date(record.startTime || record.scheduledDate).toLocaleDateString()}</span>
            ),
        },
        {
            key: 'contactName',
            title: 'Contact Name',
            dataIndex: 'contactName',
        },
        {
            key: 'phoneNumber',
            title: 'Phone Number',
            dataIndex: 'phoneNumber',
        },
        {
            key: 'transcript',
            title: 'Transcript',
            align: 'center',
            render: (_, record) => (
                <Button
                    variant='ghost'
                    size='sm'
                    iconOnly
                    isRounded
                    icon={FileText}
                    onClick={() =>
                        onViewTranscript &&
                        onViewTranscript(record.callId, {
                            name: record.contactName,
                            callDate: new Date(record.startTime || record.scheduledDate).toLocaleDateString(),
                            callTime: new Date(record.startTime || record.scheduledDate).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                            }),
                        })
                    }
                    title='View transcript'
                />
            ),
        },
        {
            key: 'recording',
            title: 'Recording',
            align: 'center',
            render: (_, record) => (
                <Button
                    variant='ghost'
                    size='sm'
                    iconOnly
                    isRounded
                    icon={SquarePlay}
                    onClick={() =>
                        onListenRecording &&
                        onListenRecording(record.callId, {
                            name: record.contactName,
                            callDate: new Date(record.startTime || record.scheduledDate).toLocaleDateString(),
                            callTime: new Date(record.startTime || record.scheduledDate).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                            }),
                        })
                    }
                    title='Listen to recording'
                />
            ),
        },
        {
            key: 'partyStage',
            title: 'Status',
            width: 160,
            render: (_, record) => (
                <StatusSelect callId={record.callId} contactId={record.contactId} currentStatus={record.partyStage} />
            ),
        },
        {
            key: 'action',
            title: 'Action',
            render: (_, record) => (
                <Button variant='primary' size='sm' icon={Phone} onClick={() => callbackHandler(record.callId)}>
                    Callback
                </Button>
            ),
        },
    ];

    return (
        <div className={styles.manualCallbacksSection}>
            <h2>Callback Requests</h2>
            <Table
                data={callsData?.data || []}
                columns={columns}
                emptyText='No call history available'
                rowKey='callId'
                size='medium'
                variant='secondary'
                isLoading={isCallsDataLoading || isCallsDataFetching}
                pagination={{
                    enabled: true,
                    onPageChange: handlePageChange,
                    onRowsPerPageChange: handleRowsPerPageChange,
                    currentPage,
                    totalPages: callsData?.pagination.totalPages || 1,
                    totalRecords: callsData?.pagination.totalRecords || 0,
                    rowsPerPage,
                }}
            />
        </div>
    );
};
